// Payroll report builder (Planning/Payroll.md). Pure: bookings × APPROVED
// timesheet entries → per-person, per-day pay lines through the same cost
// engine as actual costing (so a report's work total reconciles to the gig's
// approved actual cost), plus owed cancellation fees. The Cloud Functions that
// persist/finalize a report call this; nothing here touches storage.
import type { Booking, GigState, PayrollDay, PayrollEntry, PayrollFee, PayrollReport, Position, RateCard, TimesheetEntry } from './types.js';
import type { OTCostOutput, StandardCostOutput } from './ot-cost.js';
import { costActualsByDay } from './actuals.js';
import type { Holiday } from './standard-timekeeping.js';
import type { SharedAllocation } from './sharedDay.js';

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

// Bookings whose time is payable; matches actual costing.
const PAYABLE_BOOKING_STATES = new Set(['ACCEPTED', 'BOOKED', 'COMPLETED']);

export interface PayrollInput {
  positions: Position[];
  bookingsByPosition: Record<string, Booking[]>;
  entries: TimesheetEntry[];
  rateCards: RateCard[];
  timeZone?: string;
  holidays?: Holiday[];
  // Shared days: the APPROVED-entries allocation for bookings that belong to
  // one, plus who they share with (for the report's label). A shared booking's
  // pay is its allocated share of the pooled person-day.
  shared?: { allocation: SharedAllocation; info: Map<string, { sharedDayId: string; withGigs: string[] }> };
}

export interface PayrollData {
  entries: PayrollEntry[];
  fees: PayrollFee[];
  totalCost: number;
  warnings: string[];
  sourceEntryIds: string[];
}

function toDay(date: string, out: OTCostOutput | StandardCostOutput): PayrollDay {
  if ('ot1Hours' in out) {
    return {
      date,
      billedHours: out.hoursWorked,
      regularHours: out.regularHours,
      ot1Hours: out.ot1Hours,
      ot2Hours: out.ot2Hours,
      holiday: out.isHoliday,
      shortRest: out.insufficientRest,
      cost: round2(out.totalCost),
    };
  }
  return {
    date,
    billedHours: out.hoursWorked,
    regularHours: out.regularHours,
    ot1Hours: out.overtimeHours,
    ot2Hours: 0,
    holiday: false,
    shortRest: false,
    cost: round2(out.totalCost),
  };
}

const nameOf = (b: Booking) => b.personName ?? b.personId;

export function buildPayrollData(input: PayrollInput): PayrollData {
  const { positions, bookingsByPosition, entries, rateCards, timeZone = 'UTC', holidays } = input;
  const approvedByBooking = new Map<string, TimesheetEntry[]>();
  for (const e of entries) {
    if (e.status !== 'APPROVED') continue;
    const list = approvedByBooking.get(e.bookingId);
    if (list) list.push(e);
    else approvedByBooking.set(e.bookingId, [e]);
  }

  const rows: PayrollEntry[] = [];
  const fees: PayrollFee[] = [];
  const warnings: string[] = [];
  const sourceEntryIds: string[] = [];

  for (const pos of positions) {
    for (const booking of bookingsByPosition[pos.id] ?? []) {
      if (booking.state === 'CANCELLED') {
        // WAIVED fees cost nothing; PAID ones were already paid out.
        if (booking.cancellationFee && booking.cancellationFeeStatus === 'OWED' && booking.cancellationFee.amount > 0) {
          fees.push({
            bookingId: booking.id,
            personId: booking.personId,
            name: nameOf(booking),
            role: pos.role,
            amount: round2(booking.cancellationFee.amount),
            reason: booking.cancellationReason,
          });
        }
        continue;
      }
      if (!PAYABLE_BOOKING_STATES.has(booking.state)) continue;

      const mine = (approvedByBooking.get(booking.id) ?? []).filter(e => e.clockOut);
      if (mine.length === 0) {
        warnings.push(`${nameOf(booking)} (${pos.role}) has no recorded time`);
        continue;
      }
      const { byDay: ownByDay, hours } = costActualsByDay(booking, pos, mine, rateCards, timeZone, holidays);
      const sharedInfo = input.shared?.info.get(booking.id);
      const byDay = sharedInfo ? (input.shared!.allocation.get(booking.id) ?? ownByDay) : ownByDay;
      const days = [...byDay.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([date, out]) => toDay(date, out));
      if (days.length === 0) continue;
      const grossPay = round2([...byDay.values()].reduce((t, o) => t + o.totalCost, 0));
      // Day costs are rounded individually; the last absorbs any cent of
      // drift so the lines always sum exactly to the person's gross pay.
      const drift = round2(grossPay - days.reduce((t, d) => t + d.cost, 0));
      if (drift !== 0) days[days.length - 1].cost = round2(days[days.length - 1].cost + drift);

      rows.push({
        personId: booking.personId,
        bookingId: booking.id,
        positionId: pos.id,
        name: nameOf(booking),
        role: pos.role,
        label: pos.label,
        rateType: (booking.rateType ?? pos.rateType) === 'hourly' ? 'hourly' : 'day',
        rate: booking.rate?.amount ?? pos.rate.amount,
        workedHours: hours,
        days,
        grossPay,
        ...(sharedInfo ? { sharedDayId: sharedInfo.sharedDayId, sharedWith: sharedInfo.withGigs } : {}),
      });
      sourceEntryIds.push(...mine.map(e => e.id));
    }
  }

  const byPerson = (a: { name: string; role: string; label?: string }, b: { name: string; role: string; label?: string }) =>
    a.name.localeCompare(b.name) || a.role.localeCompare(b.role) || (a.label ?? '').localeCompare(b.label ?? '');
  rows.sort(byPerson);
  fees.sort(byPerson);
  warnings.sort();
  sourceEntryIds.sort();

  return {
    entries: rows,
    fees,
    totalCost: round2(rows.reduce((t, r) => t + r.grossPay, 0) + fees.reduce((t, f) => t + f.amount, 0)),
    warnings,
    sourceEntryIds,
  };
}

// Reasons a report can't be generated right now (empty = OK). Payroll must
// not silently omit time, so unreviewed entries block it.
export function payrollBlockers(args: {
  gigState: GigState;
  bookingsByPosition: Record<string, Booking[]>;
  entries: TimesheetEntry[];
  data: PayrollData;
  // Shared days: reasons from the OTHER linked gigs (their time still being
  // reviewed), computed by the caller, which can read them.
  sharedBlockers?: string[];
}): string[] {
  const { gigState, bookingsByPosition, entries, data } = args;
  const out: string[] = [];
  if (gigState !== 'COMPLETED' && gigState !== 'INVOICED' && gigState !== 'PAID') {
    out.push('Mark the gig completed before generating payroll.');
  }
  const payable = new Set(
    Object.values(bookingsByPosition)
      .flat()
      .filter(b => PAYABLE_BOOKING_STATES.has(b.state))
      .map(b => b.id),
  );
  const open = entries.filter(e => payable.has(e.bookingId) && e.status === 'OPEN').length;
  const pending = entries.filter(e => payable.has(e.bookingId) && e.status === 'PENDING').length;
  if (open > 0) out.push(`${open} ${open === 1 ? 'person is' : 'people are'} still clocked in.`);
  if (pending > 0) out.push(`${pending} timesheet ${pending === 1 ? 'entry still needs' : 'entries still need'} review.`);
  if (data.entries.length === 0 && data.fees.length === 0) out.push('There is no approved time or owed fee to pay.');
  out.push(...(args.sharedBlockers ?? []));
  return out;
}

// ---- CSV ---------------------------------------------------------------------

// Text cells that start with = + - @ (or a control char) are neutralized so a
// spreadsheet doesn't evaluate them as formulas (a person's name or a fee
// reason is user-entered); then quoted if they contain a delimiter.
function cell(v: string | number | boolean | undefined): string {
  if (v === undefined) return '';
  if (typeof v === 'number') return String(v);
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  let s = /^[=+\-@\t\r]/.test(v) ? `'${v}` : v;
  if (/[",\r\n]/.test(s)) s = `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function payrollToCsv(report: Pick<PayrollReport, 'entries' | 'fees' | 'totalCost'>): string {
  const rows: Array<Array<string | number | boolean | undefined>> = [
    ['Type', 'Person', 'Role', 'Rate type', 'Rate', 'Date', 'Billed hours', 'Regular hours', 'OT 1.5x hours', 'OT 2x hours', 'Holiday', 'Short rest', 'Amount'],
  ];
  for (const e of report.entries) {
    const baseRole = e.label ? `${e.role} (${e.label})` : e.role;
    const role = e.sharedWith?.length ? `${baseRole} — shared day with ${e.sharedWith.join(', ')}` : baseRole;
    for (const d of e.days) {
      rows.push(['Work', e.name, role, e.rateType, e.rate, d.date, d.billedHours, d.regularHours, d.ot1Hours, d.ot2Hours, d.holiday, d.shortRest, d.cost]);
    }
  }
  for (const f of report.fees) {
    rows.push(['Cancellation fee', f.name, f.role, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, f.amount]);
  }
  rows.push(['Total', undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, report.totalCost]);
  return rows.map(r => r.map(cell).join(',')).join('\r\n') + '\r\n';
}
